import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Divider } from '@mui/material';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { shareList } from 'store/slices/files';
import { formatToString } from 'utils/date-format';
import { useLocation } from 'react-router-dom';
import { fileTypeChecks } from 'utils/file-icon';
import { useFileDrawers } from 'hooks/useFileDrawers';
import { useFile } from 'hooks/useFile';
import { useSelectedFile } from 'hooks/useSelectedFile';
import {
  CreateOutlined,
  DeleteOutlineOutlined,
  FileCopyOutlined,
  FileDownloadOutlined,
  ShareOutlined,
  VisibilityOutlined,
  WallpaperOutlined,
} from '@mui/icons-material';
import { useMarketplaceDrawers } from 'hooks/useMarketplaceDrawers';

export const FileDetails = (props) => {
  const { close } = props;

  const dispatch = useDispatch();
  const location = useLocation();

  const { openDrawerRenameFile, openDrawerShareFile } = useFileDrawers();
  const { openDrawerEditQuoteSet } = useMarketplaceDrawers();
  const {
    handleDeleteFile,
    handleDownloadFile,
    handleDuplicateFile,
    handleSetFileAsPropertyPhoto,
    handleGoToMarketplace,
    handleEditArtwork,
  } = useFile();

  const { selectedFiles, hasSelectedMultiple } = useSelectedFile();

  const currentUser = useSelector((state) => state.auth.currentUser);
  const selectedFile = useSelector((state) => state.files.selectedFile);
  const selectedProperty = useSelector((state) => state.properties.selectedProperty);
  const sharedUserList = useSelector((state) => state.files.shareList.value);

  const isArtwork = useMemo(() => fileTypeChecks.isFileArtwork(selectedFile), [selectedFile]);
  const isQuote = useMemo(() => fileTypeChecks.isFileQuote(selectedFile), [selectedFile]);
  const isFolder = useMemo(() => selectedFile?.format === 'Folder', [selectedFile]);
  const isOwner = useMemo(() => {
    return selectedFile && currentUser && selectedFile?.owner === currentUser?.id;
  }, [selectedFile, currentUser]);
  const fileOwner = useMemo(() => {
    if (selectedFile?.ownedBy?.name) {
      return selectedFile?.ownedBy?.name;
    }
    if (selectedFile?.owner === currentUser?.id) {
      return 'Me';
    }
    return '';
  }, [selectedFile, currentUser]);
  const hasAccess = useMemo(() => {
    return selectedFile?.access && selectedFile.access.includes('Write');
  }, [selectedFile]);

  const page = useMemo(() => {
    const pathname = location.pathname;
    const id = pathname.split('/')[1] ?? '';
    return id;
  }, [location]);

  const canSetFileAsPropertyPhoto = useMemo(
    () =>
      page === 'properties' &&
      fileTypeChecks.isFileImage(selectedFile) &&
      selectedFile.oid !== selectedProperty?.preview_file_id,
    [selectedProperty, selectedFile, page]
  );

  useEffect(() => {
    if (selectedFile?.id) {
      dispatch(shareList({ fileId: selectedFile.oid }));
    }
  }, [dispatch, selectedFile]);

  return (
    <DrawerContent>
      <DrawerContent.Header
        title={hasSelectedMultiple ? `${selectedFiles.length} items selected` : selectedFile?.name}
      />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <EntityFileDetails>
            {!selectedFile?.id && !hasSelectedMultiple && <EntityFileDetails.EmptyFile />}
            {selectedFile?.id && !hasSelectedMultiple && (
              <>
                <EntityFileDetails.FileType file={selectedFile} />

                {sharedUserList.length > 0 && (
                  <>
                    <EntityFileDetails.PeopleWithAccess users={sharedUserList} />
                    <Divider />
                  </>
                )}

                <EntityFileDetails.Details title="Details">
                  {selectedFile?.format && (
                    <EntityFileDetails.DetailsItem
                      title="Type"
                      subtitle={selectedFile?.format}
                    />
                  )}
                  {fileOwner && (
                    <EntityFileDetails.DetailsItem
                      title="Owner"
                      subtitle={fileOwner}
                    />
                  )}
                  {selectedFile?.last_modified && (
                    <EntityFileDetails.DetailsItem
                      title="Modified"
                      subtitle={formatToString(selectedFile?.last_modified)}
                    />
                  )}
                  {selectedFile?.createdOn && (
                    <EntityFileDetails.DetailsItem
                      title="Created"
                      subtitle={formatToString(selectedFile?.createdOn)}
                    />
                  )}
                </EntityFileDetails.Details>

                <Divider />

                <Stack
                  direction="column"
                  spacing={1.5}
                >
                  {/* Set property photo */}
                  {(isOwner || hasAccess) && !isQuote && canSetFileAsPropertyPhoto && (
                    <EntityFileDetails.ActionButton
                      title="Set as property photo"
                      icon={<WallpaperOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleSetFileAsPropertyPhoto(selectedFile?.oid, selectedProperty?.id)}
                    />
                  )}

                  {/* Edit Artwork */}
                  {(isOwner || hasAccess) && isArtwork && (
                    <EntityFileDetails.EditButton
                      title="Edit Artwork"
                      action={() => handleEditArtwork(selectedFile)}
                    />
                  )}

                  {/* Rename File */}
                  {(isOwner || hasAccess) && !isQuote && (
                    <EntityFileDetails.ActionButton
                      title={`Rename ${selectedFile?.format}`}
                      icon={<CreateOutlined sx={{ fontSize: '22px' }} />}
                      action={openDrawerRenameFile}
                    />
                  )}
                  {(isOwner || hasAccess) && isQuote && (
                    <EntityFileDetails.ActionButton
                      title={`Rename`}
                      icon={<CreateOutlined sx={{ fontSize: '22px' }} />}
                      action={openDrawerEditQuoteSet}
                    />
                  )}

                  {/* Download File */}
                  {!isQuote && !isArtwork && !isFolder && (
                    <EntityFileDetails.ActionButton
                      title={`Download ${selectedFile?.format}`}
                      icon={<FileDownloadOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDownloadFile(selectedFile)}
                    />
                  )}

                  {/* Duplicate File */}
                  {isOwner && !isQuote && !isFolder && (
                    <EntityFileDetails.ActionButton
                      title={`Duplicate ${selectedFile?.format}`}
                      icon={<FileCopyOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDuplicateFile(selectedFile)}
                    />
                  )}

                  {/* Copy File */}
                  {!isOwner && hasAccess && !isQuote && !isFolder && (
                    <EntityFileDetails.ActionButton
                      title="Make a Copy"
                      icon={<FileCopyOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDuplicateFile(selectedFile)}
                    />
                  )}

                  {/* Share File */}
                  {isOwner && (
                    <EntityFileDetails.ActionButton
                      title={`Share ${selectedFile?.format}`}
                      icon={<ShareOutlined sx={{ fontSize: '22px' }} />}
                      action={openDrawerShareFile}
                    />
                  )}

                  {/* Delete File */}
                  {(isOwner || hasAccess) && (
                    <EntityFileDetails.ActionButton
                      title={`Delete ${selectedFile?.format}`}
                      icon={<DeleteOutlineOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDeleteFile(selectedFile, close)}
                    />
                  )}

                  {/* View in Marketplace */}
                  {isQuote && (
                    <EntityFileDetails.ActionButton
                      title="View in Marketplace"
                      icon={<VisibilityOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleGoToMarketplace(selectedFile)}
                    />
                  )}
                </Stack>
              </>
            )}
          </EntityFileDetails>
        </DrawerContent.Content>
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
